/**
 * Autosave queue reducer (design §1: saving is the workflow — no Kaydet
 * button). Pure: the screen owns timers and the actual mutations; this
 * decides WHAT is due WHEN. Geometry jobs flush before label/pose jobs
 * because a wholesale geometry write re-mints part rows and the labels must
 * land on the new rows.
 */
export type SaveJob =
  | { kind: 'geometry'; scanId: number }
  | { kind: 'labels'; partId: number }
  | { kind: 'pose'; partId: number }

export interface AutosaveState {
  pending: SaveJob[]
  dirtyAt: number | null
  inflight: boolean
  error: string | null
}

export const AUTOSAVE_DEBOUNCE_MS = 900

const sameJob = (a: SaveJob, b: SaveJob): boolean =>
  a.kind === b.kind &&
  (a.kind === 'geometry'
    ? a.scanId === (b as { scanId: number }).scanId
    : a.partId === (b as { partId: number }).partId)

export function emptyAutosave(): AutosaveState {
  return { pending: [], dirtyAt: null, inflight: false, error: null }
}

export function markDirty(state: AutosaveState, job: SaveJob, now: number): AutosaveState {
  const pending = state.pending.some((p) => sameJob(p, job))
    ? state.pending
    : [...state.pending, job]
  return { ...state, pending, dirtyAt: now, error: null }
}

export function dueJobs(state: AutosaveState, now: number): SaveJob[] | null {
  if (state.inflight || state.pending.length === 0 || state.dirtyAt === null) return null
  if (now - state.dirtyAt < AUTOSAVE_DEBOUNCE_MS) return null
  return [...state.pending].sort((a, b) =>
    a.kind === 'geometry' && b.kind !== 'geometry'
      ? -1
      : b.kind === 'geometry' && a.kind !== 'geometry'
        ? 1
        : 0,
  )
}

/**
 * Marks exactly the handed-out jobs as in flight. Taking the list (rather
 * than clearing the queue) closes a real race: a `markDirty` that lands
 * between the caller's `dueJobs` read and this update stays pending instead
 * of being silently dropped with a green "kaydedildi".
 */
export function flushStarted(state: AutosaveState, started: SaveJob[]): AutosaveState {
  return {
    ...state,
    pending: state.pending.filter((job) => !started.some((s) => sameJob(s, job))),
    inflight: true,
  }
}

export function flushSucceeded(state: AutosaveState): AutosaveState {
  return { ...state, inflight: false, dirtyAt: state.pending.length ? state.dirtyAt : null }
}

export function flushFailed(
  state: AutosaveState,
  message: string,
  retryJobs: SaveJob[],
): AutosaveState {
  const pending = [
    ...retryJobs,
    ...state.pending.filter((p) => !retryJobs.some((r) => sameJob(r, p))),
  ]
  // dirtyAt stays null so the queue holds (no hot retry loop); the next
  // markDirty — a fresh edit or an explicit retry — re-arms the debounce.
  return { ...state, pending, inflight: false, error: message, dirtyAt: null }
}

export type SaveStatus = 'kaydedildi' | 'kaydediliyor' | 'bekliyor' | 'hata'

export function saveStatus(state: AutosaveState): SaveStatus {
  if (state.error) return 'hata'
  if (state.inflight) return 'kaydediliyor'
  if (state.pending.length > 0) return 'bekliyor'
  return 'kaydedildi'
}
